import {Trans as I18nTrans, useTranslation as useI18nTranslation} from 'react-i18next'
import type {AllTranslationKeys, Namespace} from './types'
// Force our configured i18n instance to be loaded
import './index'

// Re-export Trans for components that need it
export const Trans = I18nTrans

/**
 * Typed wrapper for useTranslation hook with namespace support
 * Usage: 
 * - const { t } = useTranslation() // uses 'common' namespace by default
 * - const { t } = useTranslation('members') // uses 'members' namespace
 */
export function useTranslation<T extends Namespace = 'common'>(namespace?: T) {
  const { t: originalT, ...rest } = useI18nTranslation(namespace || 'common')
  
  // Type-safe translation function (relaxed for build compatibility)
  const t = (key: AllTranslationKeys<T> | string, options?: any): string => {
    return originalT(key as string, options)
  }
  
  return { t, ...rest }
}

/**
 * Hook for using multiple namespaces
 * Usage: const { t } = useMultipleTranslation(['common', 'members'])
 */
export function useMultipleTranslation(namespaces: Namespace[]) {
  return useI18nTranslation(namespaces)
}

/**
 * Helper to get translation with specific namespace without hook
 * Usage: translate('members', 'memberList.mainHeader')
 */
export function translate<T extends Namespace>(
  namespace: T, 
  key: AllTranslationKeys<T>, 
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any
): string {
  // Dynamic import to avoid circular dependency - this is intentional
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const i18n = require('./index').default
  return i18n.t(`${namespace}:${key}`, options)
}

/**
 * Commonly used translation namespaces as hooks
 */
export const useCommonTranslation = () => useTranslation('common')
export const useLoginTranslation = () => useTranslation('login')
export const useUsersTranslation = () => useTranslation('users')
export const useMembersTranslation = () => useTranslation('members')
export const useOrdersTranslation = () => useTranslation('orders')
export const useServicesTranslation = () => useTranslation('services')
export const useAdminTranslation = () => useTranslation('admin')
export const useErrorsTranslation = () => useTranslation('errors')
export const usePricingTranslation = () => useTranslation('pricing')
export const useMenuTranslation = () => useTranslation('menu')
export const useLedgerTranslation = () => useTranslation('ledger')
export const useBillingTranslation = () => useTranslation('billing')
export const useReportsTranslation = () => useTranslation('reports')
export const useSocialAccountTranslation = () => useTranslation('socialAccount')
